import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, TableCellLayout,
  Button, Input, Switch, Dropdown, Option, Label, Field, Spinner, Textarea, Tab, TabList,
} from "@fluentui/react-components";
import { Delete20Regular, Add20Filled, Edit20Regular, Save20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../../Services/GraphServices";
import { getMailFlows, saveMailFlow, deleteMailFlow } from "../../Services/HeedifyServices";
import { useNotification } from "../GenericComponent/NotificationSystem";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import "./MailFlowsComponent.css";

/**
 * Configuration des flux mail — l'équivalent mail d'un CallFlow.
 *
 * Avant cet écran, un flux mail ne se configurait qu'en écrivant à la main dans Cosmos. C'est
 * ainsi qu'est né le flux de QA avec deux règles de routage SANS FILE : à l'exécution,
 * `Routing.route()` saute une telle règle, les mails partaient sur la file par défaut sans leurs
 * tags, et rien nulle part ne le signalait.
 *
 * Deux conséquences sur la conception de cet écran :
 *
 *   1. La colonne « file » d'une règle est un CHOIX DANS LES FILES QUI ONT LE CANAL MAIL, jamais
 *      une saisie libre. On ne peut pas y écrire une file qui n'existe pas, ni une file voix.
 *   2. Le serveur revalide tout et REFUSE une configuration inerte. Son message est affiché tel
 *      quel : il nomme le champ fautif, ce qu'aucun « échec de l'enregistrement » ne ferait.
 *
 * L'abonnement Graph (`subscription`) n'apparaît pas ici et n'est jamais envoyé : il appartient à
 * MailFlowEngine, qui le renouvelle toutes les heures. L'écraser couperait la réception.
 */

const RULE_FIELDS = ["toRecipient", "from", "fromDomain", "subject"];
const RULE_OPS = ["equals", "contains", "regex"];

const emptyFlow = (scope) => ({
  displayName: "",
  scope: scope || "default",
  enabled: true,
  mailbox: "",
  addresses: [],
  defaultQueueId: "",
  routingRules: [],
  sla: { firstResponseMinutes: 240, businessHoursOnly: true },
  assignment: { stickyWindowHours: 72, acceptTimeoutMinutes: 10 },
  autoClose: { afterDaysWithoutReply: 7 },
  outbound: { allowCold: false },
  reopenWindowDays: 14,
  // Jamais activée par défaut : une fonctionnalité d'IA ne s'allume pas toute seule sur les
  // données d'un client qui ne l'a pas demandée.
  assist: { suggestReply: { enabled: false, prompt: "" } },
});

const MailFlowsComponent = ({ ACTIVE_TENANT, QUEUES, SCOPES_NAMES }) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const { showSuccess, showError } = useNotification();

  const [scope, setScope] = useState("default");
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);      // le flux en cours d'édition, ou null
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  // Onglets de l'édition, sur le patron de l'écran de file et du profil d'agent. Le formulaire
  // en une seule colonne mêlait la boîte surveillée, les délais, le routage et l'IA : quatre
  // sujets qui ne se règlent ni au même moment ni par les mêmes personnes.
  const [editTab, setEditTab] = useState("general");

  /**
   * Les files proposables : celles dont le canal mail est OUVERT.
   *
   * Proposer une file voix reviendrait à laisser configurer un routage que la file refusera
   * ensuite par un 409 `MAIL_CHANNEL_DISABLED` — un mail invisible pour tout le monde, sans
   * message d'erreur visible côté administrateur.
   */
  const mailQueues = useMemo(() => {
    const all = Array.isArray(QUEUES) ? QUEUES : Object.values(QUEUES || {});
    return all
      .filter((q) => q && q.channels && q.channels.mail === true)
      .filter((q) => !scope || !q.scope || q.scope === scope)
      .map((q) => ({ id: q.id || q.RowKey, name: q.queueName || q.displayName || q.id }));
  }, [QUEUES, scope]);

  const queueName = useCallback(
    (id) => (mailQueues.find((q) => q.id === id) || {}).name || id || "—",
    [mailQueues]
  );

  const load = useCallback(async () => {
    if (!ACTIVE_TENANT) return;
    setLoading(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      setFlows(await getMailFlows(ACTIVE_TENANT, scope, token));
    } catch (e) {
      showError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [ACTIVE_TENANT, scope, instance, activeAccount, showError]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    setSaving(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      // `subscription` n'est jamais renvoyé : il appartient au moteur, pas à l'écran.
      const { subscription, _rid, _self, _etag, _attachments, _ts, ...payload } = editing;
      const result = await saveMailFlow(ACTIVE_TENANT, payload, token);
      if (!result.success) {
        // Le message du serveur nomme le champ fautif ; le remplacer par un texte générique
        // rendrait l'erreur inexploitable.
        showError(result.message);
        return;
      }
      showSuccess(t("MAIL_FLOW_SAVED"));
      setEditing(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (flow) => {
    const token = await getAccessToken(instance, activeAccount);
    const result = await deleteMailFlow(ACTIVE_TENANT, flow.id, token);
    if (!result.success) { showError(result.message); return; }
    showSuccess(t("MAIL_FLOW_DELETED"));
    setToDelete(null);
    await load();
  };

  const patch = (changes) => setEditing((f) => ({ ...f, ...changes }));
  const patchRule = (index, changes) =>
    setEditing((f) => ({
      ...f,
      routingRules: f.routingRules.map((r, i) => (i === index ? { ...r, ...changes } : r)),
    }));

  const addRule = () =>
    setEditing((f) => ({
      ...f,
      routingRules: [
        ...f.routingRules,
        {
          order: f.routingRules.length + 1,
          field: "toRecipient",
          op: "equals",
          value: "",
          // Pré-rempli avec la file par défaut plutôt que vide : une règle sans file est
          // précisément le piège que cet écran existe pour empêcher.
          queueId: f.defaultQueueId || (mailQueues[0] || {}).id || "",
          priority: 0,
          tags: [],
        },
      ],
    }));

  const removeRule = (index) =>
    setEditing((f) => ({
      ...f,
      routingRules: f.routingRules.filter((_, i) => i !== index)
        .map((r, i) => ({ ...r, order: i + 1 })),
    }));

  /* ── Liste ──────────────────────────────────────────────────────────────────────────── */
  if (!editing) {
    return (
      <div className="mailflows-container">
        <div className="mailflows-header">
          <h2>{t("MAIL_FLOWS")}</h2>
          <div className="mailflows-header-actions">
            {Array.isArray(SCOPES_NAMES) && SCOPES_NAMES.length > 0 && (
              <Dropdown
                value={scope}
                selectedOptions={[scope]}
                onOptionSelect={(_, d) => setScope(d.optionValue)}
                style={{ minWidth: 180 }}
              >
                {SCOPES_NAMES.map((s) => <Option key={s} value={s}>{s}</Option>)}
              </Dropdown>
            )}
            <Button
              appearance="primary"
              icon={<Add20Filled />}
              disabled={mailQueues.length === 0}
              // Sans file à canal mail, un flux ne pourrait pas être enregistré : le serveur
              // exige un `defaultQueueId` valide. Mieux vaut le dire ici que le découvrir après
              // avoir rempli le formulaire.
              title={mailQueues.length === 0 ? t("MAIL_FLOW_NO_MAIL_QUEUE") : ""}
              onClick={() => { setEditTab("general"); setEditing(emptyFlow(scope)); }}
            >
              {t("ADD")}
            </Button>
          </div>
        </div>

        {mailQueues.length === 0 && (
          <div className="mailflows-warning">{t("MAIL_FLOW_NO_MAIL_QUEUE")}</div>
        )}

        {loading ? <Spinner label={t("LOADING")} /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t("NAME")}</TableHeaderCell>
                <TableHeaderCell>{t("MAILBOX")}</TableHeaderCell>
                <TableHeaderCell>{t("DEFAULT_QUEUE")}</TableHeaderCell>
                <TableHeaderCell>{t("ROUTING_RULES")}</TableHeaderCell>
                <TableHeaderCell>{t("ENABLED")}</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {flows.map((f) => (
                <TableRow key={f.id}>
                  <TableCell><TableCellLayout>{f.displayName}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{f.mailbox}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{queueName(f.defaultQueueId)}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{(f.routingRules || []).length}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{f.enabled ? t("YES") : t("NO")}</TableCellLayout></TableCell>
                  <TableCell>
                    <Button appearance="subtle" icon={<Edit20Regular />}
                      onClick={() => { setEditTab("general"); setEditing(f); }} />
                    <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => setToDelete(f)} />
                  </TableCell>
                </TableRow>
              ))}
              {flows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}><TableCellLayout>{t("NO_DATA_AVAILABLE")}</TableCellLayout></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <ConfirmAlert
          open={!!toDelete}
          name={toDelete?.displayName}
          onConfirm={() => onDelete(toDelete)}
          onCancel={() => setToDelete(null)}
        />
      </div>
    );
  }

  /* ── Édition ────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="mailflows-container">
      <div className="mailflows-header">
        <h2>{editing.id ? t("EDIT") : t("ADD")} — {t("MAIL_FLOWS")}</h2>
        <div className="mailflows-header-actions">
          <Button icon={<Dismiss20Regular />} onClick={() => setEditing(null)}>{t("CANCEL")}</Button>
          <Button appearance="primary" icon={<Save20Regular />} disabled={saving} onClick={onSave}>
            {saving ? <Spinner size="tiny" /> : t("SAVE")}
          </Button>
        </div>
      </div>

      <TabList
        selectedValue={editTab}
        onTabSelect={(_, d) => setEditTab(d.value)}
        className="mailflows-tabs"
      >
        <Tab value="general">{t("GENERAL")}</Tab>
        <Tab value="routing">{t("ROUTING_RULES")}</Tab>
        {/* L'IA sur son propre onglet : elle se règle une fois, par une personne qui pense au
            métier, pas au même moment que la boîte surveillée ou les délais de SLA. */}
        <Tab value="assist">{t("MAIL_ASSIST")}</Tab>
      </TabList>

      <div className="mailflows-form" hidden={editTab !== "general"}>
        <Field label={t("NAME")} required>
          <Input value={editing.displayName} onChange={(_, d) => patch({ displayName: d.value })} />
        </Field>

        <Field label={t("MAILBOX")} required
          hint={t("MAIL_FLOW_MAILBOX_HINT")}>
          <Input value={editing.mailbox} onChange={(_, d) => patch({ mailbox: d.value })} />
        </Field>

        {/* Les adresses surveillées : une par ligne. C'est cette liste que `matchedRecipient`
            compare aux destinataires du message — c'est donc elle qui rend une règle
            `toRecipient` capable de reconnaître un alias. La boîte principale doit y figurer. */}
        <Field label={t("MONITORED_ADDRESSES")} hint={t("MAIL_FLOW_ADDRESSES_HINT")}>
          <Textarea
            rows={3}
            value={(editing.addresses || []).join("\n")}
            onChange={(_, d) => patch({
              addresses: d.value.split("\n").map((s) => s.trim()).filter(Boolean),
            })}
          />
        </Field>

        <Field label={t("DEFAULT_QUEUE")} required hint={t("MAIL_FLOW_DEFAULT_QUEUE_HINT")}>
          <Dropdown
            value={queueName(editing.defaultQueueId)}
            selectedOptions={[editing.defaultQueueId]}
            onOptionSelect={(_, d) => patch({ defaultQueueId: d.optionValue })}
          >
            {mailQueues.map((q) => <Option key={q.id} value={q.id}>{q.name}</Option>)}
          </Dropdown>
        </Field>

        <div className="mailflows-inline">
          <Field label={t("SLA_FIRST_RESPONSE_MINUTES")}>
            <Input
              type="number"
              value={String(editing.sla?.firstResponseMinutes ?? "")}
              onChange={(_, d) => patch({ sla: { ...editing.sla, firstResponseMinutes: Number(d.value) } })}
            />
          </Field>
          <Field label={t("AUTO_CLOSE_DAYS")}>
            <Input
              type="number"
              value={String(editing.autoClose?.afterDaysWithoutReply ?? "")}
              onChange={(_, d) => patch({ autoClose: { ...editing.autoClose, afterDaysWithoutReply: Number(d.value) } })}
            />
          </Field>
          <Field label={t("ACCEPT_TIMEOUT_MINUTES")}>
            <Input
              type="number"
              value={String(editing.assignment?.acceptTimeoutMinutes ?? "")}
              onChange={(_, d) => patch({ assignment: { ...editing.assignment, acceptTimeoutMinutes: Number(d.value) } })}
            />
          </Field>
          <Field label={t("REOPEN_WINDOW_DAYS")}>
            <Input
              type="number"
              value={String(editing.reopenWindowDays ?? "")}
              onChange={(_, d) => patch({ reopenWindowDays: Number(d.value) })}
            />
          </Field>
        </div>

        <div className="mailflows-inline">
          <div className="mailflows-switch">
            <Label>{t("ENABLED")}</Label>
            <Switch checked={editing.enabled !== false} onChange={(_, d) => patch({ enabled: d.checked })} />
          </div>
          <div className="mailflows-switch">
            <Label>{t("SLA_BUSINESS_HOURS_ONLY")}</Label>
            <Switch
              checked={editing.sla?.businessHoursOnly === true}
              onChange={(_, d) => patch({ sla: { ...editing.sla, businessHoursOnly: d.checked } })}
            />
          </div>
          <div className="mailflows-switch">
            <Label>{t("OUTBOUND_ALLOW_COLD")}</Label>
            <Switch
              checked={editing.outbound?.allowCold === true}
              onChange={(_, d) => patch({ outbound: { ...editing.outbound, allowCold: d.checked } })}
            />
          </div>
        </div>

      </div>

      {/* ── Assistance IA ───────────────────────────────────────────────────────────────── */}
      <div className="mailflows-form" hidden={editTab !== "assist"}>
        <div className="mailflows-rules mailflows-rules-flush">
          <div className="mailflows-rules-head">
            <Label weight="semibold">{t("MAIL_ASSIST")}</Label>
            <Switch
              checked={editing.assist?.suggestReply?.enabled === true}
              onChange={(_, d) => patch({
                assist: {
                  ...(editing.assist || {}),
                  suggestReply: { ...(editing.assist?.suggestReply || {}), enabled: d.checked },
                },
              })}
            />
          </div>
          <p className="mailflows-hint">{t("MAIL_ASSIST_HINT")}</p>

          {editing.assist?.suggestReply?.enabled === true && (
            <>
              <Field label={t("MAIL_ASSIST_PROMPT")} hint={t("MAIL_ASSIST_PROMPT_HINT")}>
                <Textarea
                  rows={8}
                  className="mailflows-mono"
                  value={editing.assist?.suggestReply?.prompt || ""}
                  placeholder={t("MAIL_ASSIST_PROMPT_PLACEHOLDER")}
                  onChange={(_, d) => patch({
                    assist: {
                      ...(editing.assist || {}),
                      suggestReply: { ...(editing.assist?.suggestReply || {}), prompt: d.value },
                    },
                  })}
                />
              </Field>
              {/* Ce que la consigne métier ne peut PAS lever. Le dire ici évite qu'un
                  administrateur croie pouvoir désactiver ces garde-fous en les contredisant. */}
              <div className="mailflows-warning">{t("MAIL_ASSIST_GUARDRAILS")}</div>
            </>
          )}
        </div>

      </div>

      {/* ── Règles de routage ───────────────────────────────────────────────────────────── */}
      <div className="mailflows-form" hidden={editTab !== "routing"}>
        <div className="mailflows-rules mailflows-rules-flush">
          <div className="mailflows-rules-head">
            <Label weight="semibold">{t("ROUTING_RULES")}</Label>
            <Button icon={<Add20Filled />} onClick={addRule} size="small">{t("ADD")}</Button>
          </div>
          <p className="mailflows-hint">{t("MAIL_FLOW_RULES_HINT")}</p>

          {/* Le tableau défile horizontalement plutôt que de comprimer ses huit colonnes :
              des champs écrasés tronquent les adresses en plein milieu. */}
          <div className="mailflows-rules-table">
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell className="mailflows-rules-col-order">#</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-field">{t("FIELD")}</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-op">{t("OPERATOR")}</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-value">{t("VALUE")}</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-queue">{t("QUEUE")}</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-priority">{t("PRIORITY")}</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-tags">{t("TAGS")}</TableHeaderCell>
                <TableHeaderCell className="mailflows-rules-col-actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(editing.routingRules || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="mailflows-rules-col-order">{r.order ?? i + 1}</TableCell>
                  <TableCell>
                    <Dropdown value={r.field} selectedOptions={[r.field]}
                      onOptionSelect={(_, d) => patchRule(i, { field: d.optionValue })}>
                      {RULE_FIELDS.map((f) => <Option key={f} value={f}>{f}</Option>)}
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <Dropdown value={r.op} selectedOptions={[r.op]}
                      onOptionSelect={(_, d) => patchRule(i, { op: d.optionValue })}>
                      {RULE_OPS.map((o) => <Option key={o} value={o}>{o}</Option>)}
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <Input value={r.value || ""} onChange={(_, d) => patchRule(i, { value: d.value })} />
                  </TableCell>
                  <TableCell>
                    {/* Choix contraint, jamais de saisie libre : voir l'en-tête du fichier. */}
                    <Dropdown value={queueName(r.queueId)} selectedOptions={[r.queueId]}
                      onOptionSelect={(_, d) => patchRule(i, { queueId: d.optionValue })}>
                      {mailQueues.map((q) => <Option key={q.id} value={q.id}>{q.name}</Option>)}
                    </Dropdown>
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={String(r.priority ?? 0)}
                      onChange={(_, d) => patchRule(i, { priority: Number(d.value) })} />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={(r.tags || []).join(", ")}
                      onChange={(_, d) => patchRule(i, {
                        tags: d.value.split(",").map((s) => s.trim()).filter(Boolean),
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    <Button appearance="subtle" icon={<Delete20Regular />} onClick={() => removeRule(i)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          {(editing.routingRules || []).length === 0 && (
            <div className="mailflows-rules-empty">{t("MAIL_FLOW_NO_RULE")}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MailFlowsComponent;
